import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { OrganizerStats } from './OrganizerStats';
import { PilgrimTable } from './PilgrimTable';
import { ExportService } from './ExportService';
import { Users } from './icons';
import { collection, query, where, onSnapshot, doc, getDoc , getDocs } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { useAmountVisibility, maskAmount } from './AmountVisibilityContext';

type OrganizerStats = {
  totalPilgrims: number;
  regularPilgrims: number;
  notOwnerPilgrims: number;
  totalAmount: number;
  bonusAmount: number;
};

type PilgrimDetails = {
  pilgrimName: string;
  passportNumber: string;
  residenceCountry?: string;
};

type Props = {
  organizerId: string;
  stats: OrganizerStats;
  organizers: Record<string, any>;
  links: Record<string, any>;
  pilgrims: Record<string, PilgrimDetails>;
  guides: Record<string, any>;
  isFetchingDetails: boolean;
};

const ITEMS_PER_PAGE = 10;

export const OrganizerCard = ({ 
  organizerId, 
  stats, 
  organizers,
  links,
  pilgrims,
  guides,
  isFetchingDetails
}: Props) => {
  const { translations } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { showAmounts } = useAmountVisibility();
  const [financeConfig, setFinanceConfig] = useState({
    organizerId: organizerId,
    organizerName: organizers[organizerId]?.name || 'Default',
    baseAmount: 2000,
    bonusThreshold: 500,
    bonusAmount: 500,
    notOwnerAmount: 0
  });
  
  const organizerName = organizers[organizerId]?.name || 'Unknown';
  const organizerLinks = Object.values(links).filter(link => link.organizerId === organizerId);
  const totalPages = Math.ceil(organizerLinks.length / ITEMS_PER_PAGE);
  
  // Fetch configuration for this organizer
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Try to get custom config for this organizer
        const configRef = collection(firestore, 'FinanceConfig');
        let configQuery = query(configRef, where('organizerId', '==', organizerId));
        let configSnapshot = await getDocs(configQuery);
        
        // If no custom config, get default
        if (configSnapshot.empty) {
          configQuery = query(configRef, where('organizerId', '==', 'default'));
          configSnapshot = await getDocs(configQuery);
        }
        
        if (!configSnapshot.empty) {
          const config = configSnapshot.docs[0].data();
          setFinanceConfig({
            organizerId: config.organizerId || organizerId,
            organizerName: config.organizerName || organizers[organizerId]?.name || 'Default',
            baseAmount: config.baseAmount || 2000,
            bonusThreshold: config.bonusThreshold || 500,
            bonusAmount: config.bonusAmount || 0,
            notOwnerAmount: config.notOwnerAmount || 0
          });
        }
      } catch (error) {
        console.error('Error fetching finance config:', error);
      }
    };
    
    fetchConfig();
    
    // Set up a real-time listener for FinanceConfig collection
    const unsubscribe = onSnapshot(
      query(collection(firestore, 'FinanceConfig')),
      () => {
        // Re-fetch the configuration when changes are detected
        fetchConfig();
      }
    );
    
    return () => {
      unsubscribe();
    };
  }, [organizerId]);
  
  // Check if bonus should be applied based on pilgrim count and threshold
  // IMPORTANT: Only apply bonus if bonusAmount is greater than 0
  const hasBonusApplied = stats.totalPilgrims >= financeConfig.bonusThreshold && financeConfig.bonusAmount > 0;
  
  // Handle pagination
  const getPaginatedLinks = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return organizerLinks.slice(startIndex, endIndex);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-sm overflow-hidden"
    >
      {/* Organizer Summary Card */}
      <div 
        className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-50 rounded-lg">
              <Users className="text-primary-500" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{organizerName}</h2>
              <div className="flex items-center gap-4 mt-2">
                <div className="text-sm text-gray-500">
                  <span className="font-medium">{stats.totalPilgrims}</span> pilgrim
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium">{maskAmount(stats.totalAmount, showAmounts)}</span> SAR
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                ExportService.exportToExcel(
                  organizerId, 
                  organizerLinks, 
                  stats, 
                  organizers, 
                  pilgrims, 
                  guides, 
                  translations,
                  financeConfig
                );
              }}
              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Download size={20} />
            </button>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="border-t">
              {/* Detailed Stats */}
              <OrganizerStats stats={stats} />

              {/* Pilgrims Table */}
              <PilgrimTable 
                links={getPaginatedLinks()} 
                guides={guides} 
                pilgrims={pilgrims}
                isFetchingDetails={isFetchingDetails}
                hasBonusApplied={hasBonusApplied}
                baseAmount={financeConfig.baseAmount}
                bonusAmount={financeConfig.bonusAmount}
                notOwnerAmount={financeConfig.notOwnerAmount}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`p-1 rounded-lg ${
                        currentPage === 1
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <ChevronRight size={20} />
                    </button>

                    <span className="text-sm text-gray-700">
                      {translations?.pagination?.page || 'Page'} {currentPage}{' '}
                      {translations?.pagination?.of || 'of'} {totalPages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`p-1 rounded-lg ${
                        currentPage === totalPages
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <ChevronLeft size={20} />
                    </button>
                  </div>

                  <div className="text-sm text-gray-500">
                    {organizerLinks.length} {translations?.pagination?.items || 'pilgrim'}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};